import React from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import { Chip, IconButton } from "@outpave/ds";
import dayjs from "utils/dayjs";
import { useTranslation } from "react-i18next";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  AlertCircleIcon,
  Cancel01Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import TransactionsType from "enums/TransactionsType";
import { formatCashFull } from "utils/formatCash";
import { RenderTransactionStatus } from "components/common/RenderTransactionStatus";
import { ScoredDuplicate } from "utils/reimbursements/expenseDuplicateMatch";

interface DuplicateExpenseModalProps {
  open: boolean;
  duplicates: ScoredDuplicate[];
  onCancel: () => void;
  onEdit: () => void;
  onAcknowledge: () => void;
  onViewDetails: (transactionId: string) => void;
}

/**
 * A row of the candidate card. The label and value sit on one line, so the
 * value is a nested span rather than a sibling Typography.
 */
const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <Typography variant="label">
    {label}:{" "}
    <Typography component="span" variant="body2">
      {value}
    </Typography>
  </Typography>
);

const DuplicateExpenseModal: React.FC<DuplicateExpenseModalProps> = ({
  open,
  duplicates,
  onCancel,
  onEdit,
  onAcknowledge,
  onViewDetails,
}) => {
  const { t } = useTranslation();

  const headline =
    duplicates.length > 1
      ? t("duplicateFoundPlural", { count: duplicates.length })
      : t("duplicateFoundSingular");

  return (
    <Dialog
      open={open}
      onClose={onCancel}
      aria-label={t("duplicateModalTitle")}
      slotProps={{
        paper: { sx: { width: 520, maxWidth: "100%", maxHeight: "85vh" } },
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: "var(--space-3)",
        }}
      >
        <Box
          sx={{ display: "flex", alignItems: "center", gap: "var(--space-3)" }}
        >
          <HugeiconsIcon icon={AlertCircleIcon} size={28} />
          <Typography variant="h6">{t("duplicateModalTitle")}</Typography>
        </Box>
        <IconButton aria-label={t("Close")} onClick={onCancel}>
          <HugeiconsIcon icon={Cancel01Icon} size={20} />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--space-3)",
        }}
      >
        <Typography variant="body2">
          {headline} {t("duplicateReviewSubtext")}
        </Typography>

        {duplicates.map(({ candidate, reasons }) => {
          const merchantName =
            candidate.merchantData?.name || candidate.merchant || "-";
          const routeFrom =
            candidate.mileage?.tripFrom?.addressName ||
            candidate.mileage?.tripFrom?.address1;
          const routeTo =
            candidate.mileage?.tripTo?.addressName ||
            candidate.mileage?.tripTo?.address1;

          return (
            <Box
              key={candidate.id}
              sx={{
                display: "flex",
                flexDirection: "column",
                gap: "var(--space-2)",
                p: "var(--space-3)",
                border: "1px solid",
                borderColor: "divider",
                borderRadius: "var(--radius-lg)",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: "var(--space-2)",
                }}
              >
                <Typography variant="subtitle2">
                  {formatCashFull(
                    candidate.amount,
                    candidate.currency || "usd",
                  )}
                </Typography>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "var(--space-2)",
                  }}
                >
                  {candidate.status && (
                    <RenderTransactionStatus
                      transactionStatus={candidate.status}
                      transactionType={candidate.type}
                      smallPill
                    />
                  )}
                  <Button
                    size="small"
                    variant="primary-soft"
                    sx={{ width: "auto", px: "var(--space-3)" }}
                    onClick={() => onViewDetails(candidate.id)}
                  >
                    {t("View Details")}
                  </Button>
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "var(--space-1)",
                }}
              >
                <DetailRow
                  label={t("Date")}
                  value={dayjs(candidate.transactionDate).format("MMM D, YYYY")}
                />
                {candidate.type === TransactionsType.CASH && (
                  <DetailRow label={t("Merchant")} value={merchantName} />
                )}
                {candidate.type === TransactionsType.MILEAGE && (
                  <>
                    <DetailRow label={t("From")} value={routeFrom || "-"} />
                    <DetailRow label={t("To")} value={routeTo || "-"} />
                    {candidate.mileage?.miles != null && (
                      <DetailRow
                        label={t("Distance")}
                        value={`${candidate.mileage.miles} ${t("miles")}`}
                      />
                    )}
                  </>
                )}
                {(candidate.memo || candidate.description) && (
                  <DetailRow
                    label={t("Description")}
                    value={candidate.memo || candidate.description}
                  />
                )}
              </Box>

              {reasons.length > 0 && (
                <Box
                  sx={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "var(--space-1)",
                  }}
                >
                  {reasons.map((reason) => (
                    <Chip
                      key={reason}
                      size="sm"
                      label={t(reason)}
                      tone="warning"
                    />
                  ))}
                </Box>
              )}
            </Box>
          );
        })}
      </DialogContent>

      <DialogActions>
        <Button variant="primary-outlined" size="medium" onClick={onEdit}>
          {t("Edit")}
        </Button>
        <Button variant="primary-filled" size="medium" onClick={onAcknowledge}>
          {t("Continue")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default DuplicateExpenseModal;
